import { Info } from 'lucide-react';

import { HeroPattern } from '@/components/hero-pattern';
import { StyledLink } from '@/components/styled-link';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TypographyH1New,
  TypographyH4,
  TypographyInlineCode,
  TypographyList,
  TypographyMuted,
  TypographyP,
} from '@/components/ui/typography';

import {
  SectionHeading,
  SectionSubheading,
  TocLink,
} from '@/pages/unauthenticated/policies/components/headings';
import {
  Navigation,
  SectionFooter,
  SectionReadyToRelease,
  SignInOrDashboard,
} from '@/pages/unauthenticated/components';

import { privacyElements, privacyToc } from './constants';

export default function PrivacyPolicyPage() {
  return (
    <>
      <HeroPattern />
      <Navigation>
        <SignInOrDashboard />
      </Navigation>
      <main>
        <section className="redesign-container py-20 md:py-40">
          <div className="mx-auto max-w-7xl">
            <div className="flex justify-between">
              <TypographyH1New>Privacy Policy</TypographyH1New>
              <TypographyMuted className="text-xs lg:text-sm">
                Last updated Jan 9, 2025
              </TypographyMuted>
            </div>
            <Separator className="my-4" />
            <TypographyList className="list-none">
              {privacyToc.map((item) => {
                return (
                  <TocLink id={item.id} label={item.label} key={item.id} />
                );
              })}
            </TypographyList>
            <Separator className="my-4" />
            <TypographyP>
              At FlagSync (the &quot;Site&quot;, &quot;we&quot;, &quot;us&quot;,
              or &quot;our&quot;), we take your privacy seriously. In this
              privacy notice, we seek to explain to you in the clearest way
              possible what information FlagSync collects, uses, and discloses
              when you visit, use our services, or make a purchase from (the
              &quot;Site&quot;) or otherwise communicate with us (collectively,
              the &quot;Services&quot;).
            </TypographyP>
            <TypographyP>
              For purposes of this Privacy Policy, &quot;you&quot; and
              &quot;your&quot; means you as the user of the Services, whether
              you are a customer, website visitor, or another individual whose
              information we have collected pursuant to this Privacy Policy.
              collect, how we use it, and what rights you have in relation to
              it. describes how
            </TypographyP>
            <TypographyP>
              Please read this Privacy Policy carefully. By using and accessing
              any of the Services, you agree to the collection, use, and
              disclosure of your information as described in this Privacy
              Policy. If you do not agree to this Privacy Policy, please do not
              use or access any of the Services.
            </TypographyP>
            <TypographyP>
              If you have additional questions or require more information about
              our Privacy Policy, do not hesitate to contact at{' '}
              <StyledLink href="mailto:support@flagsync.com">
                support@flagsync.com
              </StyledLink>
              .
            </TypographyP>
            <SectionHeading id="changes">
              Changes to This Privacy Policy
            </SectionHeading>
            <TypographyP>
              We may update this Privacy Policy from time to time, including to
              reflect changes to our practices or for other operational, legal,
              or regulatory reasons. We will post the revised Privacy Policy on
              the Site, update the &quot;Last updated&quot; date and take any
              other steps required by applicable law.
            </TypographyP>
            <SectionHeading id="information-collection">
              How We Collect and Use Your Personal Information
            </SectionHeading>
            <TypographyP>
              To provide the Services, we collect and have collected over the
              past 12 months personal information about you from a variety of
              sources, as set out below. The information that we collect and use
              varies depending on how you interact with us.
            </TypographyP>
            <SectionSubheading>
              Information We Collect Directly from You
            </SectionSubheading>
            <TypographyP as="div">
              We collect personal information that you voluntarily provide to us
              when you register on the Services, or when you contact us. The
              personal information that we collect depends on the context of
              your interactions with us and the Services, and can include the
              following:
              <TypographyList>
                <li>Basic contact details including your name and email.</li>
                <li>Account information including your email and password</li>
                <li>
                  Customer support information including the information you
                  choose to include in communications with us, for example, when
                  sending a message to{' '}
                  <StyledLink href="mailto:support@flagsync.com?subject=Ownership transfer request">
                    support@flagsync.com
                  </StyledLink>
                  .
                </li>
              </TypographyList>
            </TypographyP>
            <TypographyP>
              We do not process sensitive information, or sell it to third
              parties.
            </TypographyP>
            <SectionSubheading>
              Information Automatically Collected
            </SectionSubheading>
            <TypographyP as="div">
              We automatically collect certain information when you visit, use,
              or navigate the Services. This information may include device and
              usage information, such as your IP address, browser and device
              characteristics, operating system, language preferences, referring
              URLs, device name, country, location, information about how and
              when you use our Services, and other technical information. This
              information is primarily needed to maintain the security and
              operation of our Services, and for our internal analytics and
              reporting purposes.
            </TypographyP>
            <SectionSubheading>
              Information We Collect through Cookies
            </SectionSubheading>
            <TypographyP>
              We also automatically collect certain information about your
              interaction with the Services (&quot;Usage Data&quot;). To do
              this, we may use cookies, pixels and similar technologies
              (&quot;Cookies&quot;). Usage Data may include information about
              how you access and use our Site and your account, including device
              information, browser information, information about your network
              connection, your IP address and other information regarding your
              interaction with the Services.
            </TypographyP>
            <TypographyP>
              We use Cookies to power and improve our Site and our Services
              (including to remember your actions and preferences), to run
              analytics and better understand user interaction with the Services
              (in our legitimate interests to administer, improve and optimize
              the Services)
            </TypographyP>
            <TypographyP>
              Most browsers automatically accept Cookies by default, but you can
              choose to set your browser to remove or reject Cookies through
              your browser controls. Please keep in mind that removing or
              blocking Cookies can negatively impact your user experience and
              may cause some of the Services, including certain features and
              general functionality, to work incorrectly or no longer be
              available.
            </TypographyP>
            <SectionSubheading>
              Information We Obtain from Third Parties
            </SectionSubheading>
            <TypographyP as="div">
              Finally, we may obtain information about you from third parties,
              including from vendors and service providers who may collect
              information on our behalf, such as{' '}
              <StyledLink href="https://www.stripe.com">Stripe</StyledLink>, who
              is our payment processor.
            </TypographyP>
            <TypographyH4 className="pt-8">
              Google OAuth Integration
            </TypographyH4>
            <TypographyP as="div">
              We also integrate with{' '}
              <StyledLink href="https://www.google.com">Google</StyledLink>, who
              we use as an OAuth provider. When you register or login to
              FlagSync using Google, we request the following scopes:
              <Table className="mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead>Scope</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TypographyInlineCode>
                        ./auth/userinfo.email
                      </TypographyInlineCode>
                    </TableCell>
                    <TableCell>
                      We use your Google email as unique identifier in our
                      Service. We may use this email to send verification links,
                      password reset links, and general communication regarding
                      our Services.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TypographyInlineCode>
                        ./auth/userinfo.profile
                      </TypographyInlineCode>
                    </TableCell>
                    <TableCell>
                      We use your Google profile information to obtain your
                      given and family name, Google ID, as well a your profile
                      picture you may have uploaded to Google, to be used as
                      your avatar in FlagSync.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Alert variant="primary">
                <Info className="w-4 h-4" />
                <AlertTitle>Storage of Google information</AlertTitle>
                <AlertDescription>
                  We store in our database a subset of your Google information,
                  such as email, Google ID, profile picture URL, and given and
                  family name, to provide you with the Services. We do not store
                  any other information from Google.
                </AlertDescription>
              </Alert>
            </TypographyP>
            <TypographyP>
              Any information we obtain from third parties will be treated in
              accordance with this Privacy Policy. We are not responsible or
              liable for the accuracy of the information provided to us by third
              parties and are not responsible for any third party&apos;s
              policies or practices.
            </TypographyP>
            <SectionSubheading>
              How We Use Your Personal Information
            </SectionSubheading>
            <TypographyP as="div">
              <TypographyList>
                <li>
                  <span className="font-semibold">
                    Providing Products and Services.
                  </span>{' '}
                  We use your personal information to provide you with the
                  Services in order to perform our contract with you.
                </li>
                <li>
                  <span className="font-semibold">
                    Marketing and Advertising.
                  </span>{' '}
                  We use your personal information for marketing and promotional
                  purposes, such as to send marketing, advertising or
                  promotional communications to you by email.
                </li>
                <li>
                  <span className="font-semibold">Communicating with you.</span>{' '}
                  We may process your information to respond to your inquiries
                  and solve any potential issues you might have with the
                  requested service.
                </li>
                <li>
                  <span className="font-semibold">
                    Security and Fraud Prevention.
                  </span>{' '}
                  We use your personal information to detect, investigate or
                  take action regarding possible fraudulent, illegal or
                  malicious activity. If you choose to use the Services and
                  register an account, you are responsible for keeping your
                  account credentials safe. We highly recommend that you do not
                  share your username, password, or other access details with
                  anyone else. If you believe your account has been compromised,
                  please contact us immediately.
                </li>
              </TypographyList>
            </TypographyP>
            <SectionSubheading>
              How we disclose your personal information
            </SectionSubheading>
            <TypographyP as="div">
              <span className="font-semibold">
                Vendors, Consultants, and Other Third-Party Service Providers.
              </span>{' '}
              We may share your data with third-party vendors who perform
              services for us or on our behalf and require access to such
              information to do that work, however, in most cases, this is
              unnecessary. The third parties we may share personal information
              with are as follows:
              <TypographyList>
                <li>Amazon Web Services (AWS)</li>
                <li>Stripe</li>
                <li>Google Fonts</li>
                <li>Google Analytics</li>
                <li>Datadog</li>
                <li>Cloudflare</li>
                <li>Vercel</li>
                <li>Supabase</li>
              </TypographyList>
            </TypographyP>
            <TypographyP>
              Additionally, we may disclose your personal information in
              connection with a business transaction such as a merger or
              bankruptcy, to comply with any applicable legal obligations
              (including to respond to subpoenas, search warrants and similar
              requests), to enforce any applicable terms of service, and to
              protect or defend the Services, our rights, and the rights of our
              users or others.
            </TypographyP>
            <SectionHeading id="third-party">
              Third-party Websites and Links
            </SectionHeading>
            <TypographyP>
              Although uncommon, our Site may provide links to websites or other
              online platforms operated by third parties. If you follow links to
              sites not affiliated or controlled by us, you should review their
              privacy and security policies and other terms and conditions. We
              do not guarantee and are not responsible for the privacy or
              security of such sites, including the accuracy, completeness, or
              reliability of information found on these sites. Information you
              provide on public or semi-public venues, including information you
              share on third-party social networking platforms may also be
              viewable by other users of the Services and/or users of those
              third-party platforms without limitation as to its use by us or by
              a third party. Our inclusion of such links does not, by itself,
              imply any endorsement of the content on such platforms or of their
              owners or operators, except as disclosed on the Services.
            </TypographyP>
            <SectionHeading id="childrens-data">
              Children&apos;s Data
            </SectionHeading>
            <TypographyP>
              The Services are not intended to be used by children, and we do
              not knowingly collect any personal information about children. If
              you are the parent or guardian of a child who has provided us with
              their personal information, you may contact us using the contact
              details set out below to request that it be deleted.
            </TypographyP>
            <SectionHeading id="data-retention">
              Security and Retention of Your Information
            </SectionHeading>
            <TypographyP>
              Please be aware that no security measures are perfect or
              impenetrable, and we cannot guarantee &quot;perfect
              security.&quot; In addition, any information you send to us may
              not be secure while in transit. We recommend that you do not use
              unsecure channels to communicate sensitive or confidential
              information to us.
            </TypographyP>
            <TypographyP>
              How long we retain your personal information depends on different
              factors, such as whether we need the information to maintain your
              account, to provide the Services, comply with legal obligations,
              resolve disputes or enforce other applicable contracts and
              policies.
            </TypographyP>
            <SectionHeading id="your-rights">
              Your Rights and Choices
            </SectionHeading>
            <TypographyP>
              Depending on where you live, you may have some or all of the
              rights listed below in relation to your personal information.
              However, these rights are not absolute, may apply only in certain
              circumstances and, in certain cases, we may decline your request
              as permitted by law.
            </TypographyP>
            <TypographyP as="div">
              <TypographyList>
                <li>
                  <span className="font-semibold">Right to Access / Know.</span>{' '}
                  You may have a right to request access to personal information
                  that we hold about you, including details relating to the ways
                  in which we use and share your information.
                </li>
                <li>
                  <span className="font-semibold">Right to Delete.</span> You
                  may have a right to request that we delete personal
                  information we maintain about you.
                </li>
                <li>
                  <span className="font-semibold">Right to Correct.</span> You
                  may have a right to request that we correct inaccurate
                  personal information we maintain about you.
                </li>
                <li>
                  <span className="font-semibold">Right of Portability.</span>{' '}
                  You may have a right to receive a copy of the personal
                  information we hold about you and to request that we transfer
                  it to a third party, in certain circumstances and with certain
                  exceptions.
                </li>
                <li>
                  <span className="font-semibold">
                    Right to Withdrawal of Consent.
                  </span>{' '}
                  Where we rely on consent to process your personal information,
                  you may have the right to withdraw this consent.
                </li>
              </TypographyList>
            </TypographyP>
            <TypographyP>
              You may exercise any of these rights where indicated on our Site
              or by contacting us using the contact details provided below.
            </TypographyP>
            <TypographyP>
              We will not discriminate against you for exercising any of these
              rights. We may need to collect information from you to verify your
              identity, such as your email address or account information,
              before providing a substantive response to the request. In
              accordance with applicable laws, You may designate an authorized
              agent to make requests on your behalf to exercise your rights.
              Before accepting such a request from an agent, we will require
              that the agent provide proof you have authorized them to act on
              your behalf, and we may need you to verify your identity directly
              with us. We will respond to your request in a timely manner as
              required under applicable law.
            </TypographyP>
            <SectionHeading id="international-users">
              International Users
            </SectionHeading>
            <TypographyP>
              Our servers are located in the United States. If you are accessing
              our Services from outside the United States, please be aware that
              your information may be transferred to, stored, and processed by
              us in our facilities and by those third parties with whom we may
              share your personal information.
            </TypographyP>
            <TypographyP>
              If you are a resident in the European Economic Area (EEA) or
              United Kingdom (UK), then these countries may not necessarily have
              data protection laws or other similar laws as comprehensive as
              those in your country. However, we will take all necessary
              measures to protect your personal information in accordance with
              this privacy notice and applicable law.
            </TypographyP>
            <SectionHeading id="california-residents">
              California Residents
            </SectionHeading>
            <TypographyP>
              California Civil Code Section 1798.83, also known as the
              &quot;Shine The Light&quot; law, permits our users who are
              California residents to request and obtain from us, once a year
              and free of charge. If you are a California resident and would
              like to make such a request, please submit your request to{' '}
              <StyledLink href="mailto:support@flagsync.com?subject=Ownership transfer request">
                support@flagsync.com
              </StyledLink>
              .
            </TypographyP>
            <SectionSubheading>CCPA Privacy Notice</SectionSubheading>
            <TypographyP as="div">
              The California Code of Regulations defines a &quot;resident&quot;
              as:
              <TypographyList>
                <li>
                  Every individual who is in the State of California for other
                  than a temporary or transitory purpose.
                </li>
                <li>
                  Every individual who is domiciled in the State of California
                  who is outside the State of California for a temporary or
                  transitory purpose.
                </li>
              </TypographyList>
            </TypographyP>
            <TypographyP>
              All other individuals are defined as &quot;non-residents.&quot; If
              this definition of &quot;resident&quot; applies to you, we must
              adhere to certain rights and obligations regarding your personal
              information.
            </TypographyP>
            <TypographyH4 className="mt-8">
              What categories of personal information do we collect?
            </TypographyH4>
            <Table className="mt-2">
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Examples</TableHead>
                  <TableHead>Collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {privacyElements.map((element, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{element.category}</TableCell>
                      <TableCell>{element.examples}</TableCell>
                      <TableCell>{element.collected}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TypographyP as="div">
              We may also collect other personal information outside of these
              categories through instances where you interact with us in person,
              online, or by phone or mail in the context of:
              <TypographyList>
                <li>Receiving help through our customer support channels;</li>
                <li>Participation in customer surveys or contests; and</li>
                <li>
                  Facilitation in the delivery of our Services and to respond to
                  your inquiries.
                </li>
              </TypographyList>
            </TypographyP>
            <TypographyH4 className="mt-8">
              How do we use and share your personal information?
            </TypographyH4>
            <TypographyP>
              You may contact us by email at{' '}
              <StyledLink href="mailto:support@flagsync.com?subject=Ownership transfer request">
                support@flagsync.com
              </StyledLink>
              .
            </TypographyP>
            <TypographyP>
              If you are using an authorized agent to exercise your right to opt
              out we may deny a request if the authorized agent does not submit
              proof that they have been validly authorized to act on your
              behalf.
            </TypographyP>
            <TypographyH4 className="mt-8">
              Will your information be shared with anyone else?
            </TypographyH4>
            <TypographyP>
              We may disclose your personal information with our service
              providers pursuant to a written contract between us and each
              service provider. Each service provider is a for-profit entity
              that processes the information on our behalf, following the same
              strict privacy protection obligations mandated by the CCPA.
            </TypographyP>
            <TypographyP>
              We may use your personal information for our own business
              purposes, such as for undertaking internal research for
              technological development and demonstration. This is not
              considered to be &quot;selling&quot; of your personal information.
            </TypographyP>
            <TypographyP>
              FlagSync has not sold or shared any personal information to third
              parties for a business or commercial purpose in the preceding
              twelve (12) months.
            </TypographyP>
            <TypographyH4 className="mt-8">Verification process</TypographyH4>
            <TypographyP>
              Upon receiving your request, we will need to verify your identity
              to determine you are the same person about whom we have the
              information in our system. These verification efforts require us
              to ask you to provide information so that we can match it with
              information you have previously provided us. For instance,
              depending on the type of request you submit, we may ask you to
              provide certain information so that we can match the information
              you provide with the information we already have on file, or we
              may contact you through a communication method (e.g., phone or
              email) that you have previously provided to us. We may also use
              other verification methods as the circumstances dictate.
            </TypographyP>
            <TypographyP>
              We will only use personal information provided in your request to
              verify your identity or authority to make the request. To the
              extent possible, we will avoid requesting additional information
              from you for the purposes of verification. However, if we cannot
              verify your identity from the information already maintained by
              us, we may request that you provide additional information for the
              purposes of verifying your identity and for security or
              fraud-prevention purposes. We will delete such additionally
              provided information as soon as we finish verifying you.
            </TypographyP>
            <SectionHeading id="contact-us">Contact us</SectionHeading>
            <TypographyP>
              If you have additional questions or require more information about
              our Privacy Policy, do not hesitate to contact at{' '}
              <StyledLink href="mailto:support@flagsync.com">
                support@flagsync.com
              </StyledLink>
              .
            </TypographyP>
          </div>
        </section>
        <SectionReadyToRelease />
        <SectionFooter />
      </main>
    </>
  );
}
